/**
 * useTableSort — Hook reutilizable para ordenar listas tipo hoja de cálculo.
 *
 * Click en cabecera → asc · click otra vez → desc · click otra vez → sin orden.
 *
 * Uso:
 *   const { sorted, sortBy, sortDir, requestSort, sortIcon } = useTableSort(items);
 *   <th onClick={() => requestSort("nombre")}>Nombre {sortIcon("nombre")}</th>
 *   {sorted.map(...)}
 */
import { useState, useMemo } from "react";

export function useTableSort(items, initial = { by: null, dir: null }) {
  const [sortBy, setSortBy] = useState(initial.by);
  const [sortDir, setSortDir] = useState(initial.dir);  // 'asc' | 'desc' | null

  const requestSort = (field) => {
    if (sortBy !== field) { setSortBy(field); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    if (sortDir === "desc") { setSortBy(null); setSortDir(null); return; }
    setSortDir("asc");
  };

  const sorted = useMemo(() => {
    if (!sortBy || !sortDir) return items;
    const arr = [...items];
    arr.sort((a, b) => {
      const va = getVal(a, sortBy);
      const vb = getVal(b, sortBy);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return va - vb;
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      return sa.localeCompare(sb, "es");
    });
    if (sortDir === "desc") arr.reverse();
    return arr;
  }, [items, sortBy, sortDir]);

  const sortIcon = (field) => {
    if (sortBy !== field) return "↕";
    return sortDir === "asc" ? "▲" : "▼";
  };

  return { sorted, sortBy, sortDir, requestSort, sortIcon };
}

function getVal(obj, field) {
  // Soporta dotted paths "cadena.nombre"
  return field.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}
